import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, TrendingUp, Building2, FileText, Globe2, Shield, Zap, ArrowLeftRight, Mountain, Truck, Landmark, Banknote, Wheat, Cpu } from 'lucide-react';
import { motion } from 'framer-motion';
import Lenis from '@studio-freight/lenis';

const Home = () => {
  const { t, language } = useLanguage();
  const lenisRef = useRef(null);

  useEffect(() => {
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });

    function raf(time) {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenisRef.current?.destroy();
    };
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="min-h-screen">
      <section 
        className="relative h-screen flex items-center justify-center overflow-hidden grain-texture"
        data-testid="hero-section"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1676647018922-9b9e8ec7622d?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.h1 
            {...fadeInUp}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
            data-testid="hero-title"
          >
            {t.welcomeMessage}
          </motion.h1>
          <motion.p 
            {...fadeInUp}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-base sm:text-lg lg:text-xl max-w-3xl mx-auto mb-8 opacity-90"
            data-testid="hero-subtitle"
          >
            {t.welcomeSubtitle}
          </motion.p>
          <motion.div 
            {...fadeInUp}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link 
              to="/investment-guide" 
              data-testid="get-started-btn"
              className="btn-primary inline-flex items-center space-x-2"
            >
              <span>{t.getStarted}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link 
              to="/contact" 
              data-testid="contact-btn"
              className="btn-secondary inline-flex items-center space-x-2"
            >
              <span>{t.contactUs}</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Clear Messaging Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary via-primary to-[#0f1d3a] relative overflow-hidden" data-testid="clear-messaging-section">
        {/* Background decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-secondary rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
              <ArrowLeftRight className="w-4 h-4" />
              {t.bridgingMarkets || 'Bridging Markets'}
            </span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Dubai to Africa */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-lg hover:bg-white/15 transition-all duration-300 group"
              data-testid="dubai-to-africa-card"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🏙️</span>
                </div>
                <ArrowRight className="w-6 h-6 text-secondary" />
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🌍</span>
                </div>
                <span className="ml-auto text-xs font-mono text-white/60 bg-white/10 px-3 py-1 rounded-full">
                  {t.dubaiToAfrica || 'Dubai → Central Africa'}
                </span>
              </div>
              <p className="text-white text-lg leading-relaxed">
                {t.clearMessagePart1 || 'We help Dubai-based companies and investors successfully enter and navigate the high-potential, complex markets of Central Africa.'}
              </p>
            </motion.div>

            {/* Africa to Dubai */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-lg hover:bg-white/15 transition-all duration-300 group"
              data-testid="africa-to-dubai-card"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🌍</span>
                </div>
                <ArrowRight className="w-6 h-6 text-secondary" />
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🏙️</span>
                </div>
                <span className="ml-auto text-xs font-mono text-white/60 bg-white/10 px-3 py-1 rounded-full">
                  {t.africaToDubai || 'Africa → Dubai'}
                </span>
              </div>
              <p className="text-white text-lg leading-relaxed">
                {t.clearMessagePart2 || 'We also assist African companies in establishing and scaling their operations in Dubai.'}
              </p>
            </motion.div>
          </div>

          {/* Hashtags */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-10 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
            data-testid="home-hashtags"
          >
            {['#DubaiBusiness', '#AfricaTrade', '#UAE', '#DRC', '#Angola', '#InvestInAfrica'].map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-mono font-medium text-white/90 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-secondary/30 hover:border-secondary/50 hover:text-white transition-all duration-300 cursor-default"
              >
                {tag}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Strongest Sectors Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden" data-testid="strongest-sectors-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/5 text-primary rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              <Zap className="w-3.5 h-3.5" />
              {t.sectorsBadge || (language === 'fr' ? 'Nos Forces' : 'Our Strengths')}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              {t.sectorsTitle || (language === 'fr' ? 'Nos Secteurs les Plus Solides' : 'Our Strongest Sectors')}
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
              {t.sectorsSubtitle || (language === 'fr'
                ? 'Six domaines où nous livrons une expertise éprouvée entre le Golfe et l\'Afrique Centrale.'
                : 'Six domains where we deliver proven expertise between the Gulf and Central Africa.')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {[
              { icon: Mountain, key: 'mining', color: '#DB242C' },
              { icon: Truck, key: 'logistics', color: '#1B325F' },
              { icon: Landmark, key: 'infrastructure', color: '#DB242C' },
              { icon: Banknote, key: 'trade-finance', color: '#1B325F' },
              { icon: Wheat, key: 'agri-business', color: '#DB242C' },
              { icon: Cpu, key: 'digital-transformation', color: '#1B325F' },
            ].map((sector, idx) => {
              const Icon = sector.icon;
              const labels = {
                fr: {
                  'mining': { name: 'Mines & Ressources', desc: 'Cobalt, cuivre, or, diamants et minerais stratégiques.' },
                  'logistics': { name: 'Logistique', desc: 'Corridors DRC–Angola–UAE et solutions de supply chain.' },
                  'infrastructure': { name: 'Infrastructure', desc: 'Routes, ports, énergie et grands projets publics.' },
                  'trade-finance': { name: 'Finance Commerciale', desc: 'Lettres de crédit, garanties et financement export.' },
                  'agri-business': { name: 'Agribusiness', desc: 'Production, transformation et export agroalimentaire.' },
                  'digital-transformation': { name: 'Transformation Numérique', desc: 'Fintech, e-gouvernance et modernisation IT.' },
                },
                en: {
                  'mining': { name: 'Mining & Resources', desc: 'Cobalt, copper, gold, diamonds and strategic minerals.' },
                  'logistics': { name: 'Logistics', desc: 'DRC–Angola–UAE corridors and supply chain solutions.' },
                  'infrastructure': { name: 'Infrastructure', desc: 'Roads, ports, energy and major public projects.' },
                  'trade-finance': { name: 'Trade Finance', desc: 'Letters of credit, guarantees and export financing.' },
                  'agri-business': { name: 'Agri-business', desc: 'Production, processing and agri-food exports.' },
                  'digital-transformation': { name: 'Digital Transformation', desc: 'Fintech, e-governance and IT modernization.' },
                },
              };
              const lang = language === 'fr' ? 'fr' : 'en';
              const label = labels[lang][sector.key];
              const exploreLabel = lang === 'fr' ? 'Explorer ce secteur' : 'Explore this sector';
              return (
                <motion.div
                  key={sector.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6 }}
                  data-testid={`sector-card-${sector.key}`}
                >
                  <Link
                    to={`/sectors/${sector.key}`}
                    aria-label={`${label.name} — ${exploreLabel}`}
                    className="group relative block bg-card border border-border/60 rounded-xl p-6 sm:p-7 overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-primary/40 h-full"
                  >
                    {/* Accent bar */}
                    <div
                      className="absolute top-0 left-0 h-1 w-16 group-hover:w-full transition-all duration-500"
                      style={{ backgroundColor: sector.color }}
                    />

                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center mb-5 transition-transform group-hover:scale-110 group-hover:rotate-3"
                      style={{
                        backgroundColor: `${sector.color}15`,
                        color: sector.color,
                      }}
                    >
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </div>

                    <h3 className="font-serif text-xl font-bold text-foreground mb-2">
                      {label.name}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                      {label.desc}
                    </p>

                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider opacity-0 group-hover:opacity-100 translate-x-[-4px] group-hover:translate-x-0 transition-all duration-300"
                      style={{ color: sector.color }}>
                      {exploreLabel}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>

                    {/* Subtle number */}
                    <span className="absolute top-4 right-5 font-mono text-xs text-muted-foreground/40">
                      0{idx + 1}
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="services-overview">
        <div className="max-w-7xl mx-auto">
          <div className="tetris-grid">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="feature-tall bg-card border border-border/40 p-8 hover:border-primary/50 transition-colors relative overflow-hidden group"
              data-testid="investment-card"
            >
              <div className="relative z-10">
                <TrendingUp className="h-12 w-12 text-secondary mb-4" />
                <h3 className="font-serif text-2xl font-bold mb-4 text-foreground">
                  {language === 'fr' ? 'Opportunités d\'Investissement Exceptionnelles' : 'Exceptional Investment Opportunities'}
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {language === 'fr' 
                    ? 'Découvrez les opportunités exceptionnelles en RDC avec First Business Group SARLU : 70% des réserves mondiales de cobalt, vastes gisements de cuivre et d\'or, et un potentiel de croissance immense. Nous transformons ces opportunités en succès pour nos clients internationaux.'
                    : 'Discover exceptional opportunities in DRC with First Business Group SARLU: 70% of global cobalt reserves, vast copper and gold deposits, and immense growth potential. We transform these opportunities into success for our international clients.'}
                </p>
                <Link to="/investment-guide" className="inline-flex items-center text-primary font-medium hover:underline">
                  {t.learnMore} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="feature-box bg-card border border-border/40 p-8 hover:border-primary/50 transition-colors relative overflow-hidden group"
              data-testid="company-formation-card"
            >
              <Building2 className="h-10 w-10 text-secondary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3 text-foreground">
                {language === 'fr' ? 'Création d\'Entreprise' : 'Company Formation'}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {language === 'fr' 
                  ? 'Guide complet pour créer votre entreprise en RDC'
                  : 'Complete guide to forming your company in DRC'}
              </p>
              <Link to="/company-formation" className="text-primary text-sm font-medium hover:underline">
                {t.learnMore} →
              </Link>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="feature-box bg-card border border-border/40 p-8 hover:border-primary/50 transition-colors relative overflow-hidden group"
              data-testid="legal-protection-card"
            >
              <Shield className="h-10 w-10 text-secondary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3 text-foreground">
                {language === 'fr' ? 'Protection Légale' : 'Legal Protection'}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {language === 'fr' 
                  ? 'Garanties et protection pour vos investissements'
                  : 'Guarantees and protection for your investments'}
              </p>
              <Link to="/investment-guide" className="text-primary text-sm font-medium hover:underline">
                {t.learnMore} →
              </Link>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="feature-box bg-card border border-border/40 p-8 hover:border-primary/50 transition-colors relative overflow-hidden group"
              data-testid="consulting-card"
            >
              <FileText className="h-10 w-10 text-secondary mb-4" />
              <h3 className="font-serif text-xl font-bold mb-3 text-foreground">
                {language === 'fr' ? 'Conseil Commercial' : 'Business Consulting'}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {language === 'fr' 
                  ? 'Expertise professionnelle pour votre succès'
                  : 'Professional expertise for your success'}
              </p>
              <Link to="/services" className="text-primary text-sm font-medium hover:underline">
                {t.learnMore} →
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-white" data-testid="why-drc-section">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl font-bold mb-6"
          >
            {t.whyInvestDRC}
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-lg max-w-3xl mx-auto mb-12 opacity-90"
          >
            {t.whyInvestDesc}
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Globe2 className="h-16 w-16 mx-auto mb-4 text-secondary" />
              <h3 className="font-serif text-xl font-bold mb-2">70%</h3>
              <p className="opacity-90">{language === 'fr' ? 'Réserves mondiales de cobalt' : 'Global Cobalt Reserves'}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Zap className="h-16 w-16 mx-auto mb-4 text-secondary" />
              <h3 className="font-serif text-xl font-bold mb-2">100 GW</h3>
              <p className="opacity-90">{language === 'fr' ? 'Potentiel hydroélectrique' : 'Hydroelectric Potential'}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <TrendingUp className="h-16 w-16 mx-auto mb-4 text-secondary" />
              <h3 className="font-serif text-xl font-bold mb-2">80M</h3>
              <p className="opacity-90">{language === 'fr' ? 'Hectares de terres arables' : 'Hectares Arable Land'}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl font-bold mb-6 text-foreground"
          >
            {t.readyToStart}
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-lg text-muted-foreground mb-8"
          >
            {t.readyToStartDesc}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Link 
              to="/contact" 
              data-testid="cta-contact-btn"
              className="btn-primary inline-flex items-center space-x-2"
            >
              <span>{t.contactUs}</span>
              <ArrowRight className="h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;